import React, { useState, useMemo, useEffect } from "react";
import { X, Lock, AlertCircle, Loader2 } from "lucide-react";
import { Button, TimePicker24h } from "../../ui-v2";
import { cn } from "../../../../lib/cn";

/**
 * BloquearFranjaModal — modal para crear bloqueos de técnico.
 *
 * Soporta dos modos:
 *   - "franja": bloquea el rango horario completo de la franja
 *   - "slot":   bloquea solo un slot puntual
 *
 * Y tres recurrencias:
 *   - "solo_dia":     bloquea solo el día indicado
 *   - "todos_campana": bloquea todos los días de la semana coincidentes
 *                      hasta `fechaLimiteCampana`
 *   - "rango":        bloquea todos los días de la semana coincidentes
 *                      en un rango personalizado
 *
 * Si `esAdmin=true`, muestra selector de técnico (admin puede bloquear
 * a cualquier técnico de la campaña). Si false, el técnico autenticado
 * solo puede bloquearse a sí mismo.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   onSave: (bloqueos: array) => Promise<void>
 *     → `bloqueos` es un array `[{ persona_id, fecha, hora_inicio, hora_fin, motivo? }]`
 *       listo para enviar al endpoint batch del backend.
 *   modo: "franja" | "slot"
 *   fecha: "YYYY-MM-DD"
 *   horaInicio, horaFin: "HH:MM" o "HH:MM:SS"
 *   esAdmin: bool
 *   tecnicos: [{ id, nombre }] | null  → lista de técnicos (solo si esAdmin)
 *   tecnicoIdActual: number  → preseleccionado (mi id si soy técnico,
 *     o el técnico filtrado si soy admin con filtro)
 *   fechaLimiteCampana: "YYYY-MM-DD"  → para opción "todos_campana"
 */

const DIAS_LABEL = ["", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

function fechaADiaISO(yyyymmdd) {
  if (!yyyymmdd) return null;
  const d = new Date(`${yyyymmdd}T12:00:00`);
  const js = d.getDay();
  return js === 0 ? 7 : js;
}

function fechasRecurrentes(desde, hasta, diaISO) {
  if (!desde || !hasta || !diaISO) return [];
  const inicio = new Date(`${desde}T12:00:00`);
  const fin = new Date(`${hasta}T12:00:00`);
  if (inicio > fin) return [];
  const fechas = [];
  const cur = new Date(inicio);
  while (cur <= fin) {
    const js = cur.getDay();
    const iso = js === 0 ? 7 : js;
    if (iso === diaISO) {
      const yyyy = cur.getFullYear();
      const mm = String(cur.getMonth() + 1).padStart(2, "0");
      const dd = String(cur.getDate()).padStart(2, "0");
      fechas.push(`${yyyy}-${mm}-${dd}`);
    }
    cur.setDate(cur.getDate() + 1);
  }
  return fechas;
}

function normalizarHora(v) {
  if (!v) return "";
  return String(v).length === 5 ? `${v}:00` : String(v);
}

// ────────────────────────────────────────────────────────────────
//  Componente
// ────────────────────────────────────────────────────────────────

export default function BloquearFranjaModal({
  open,
  onClose,
  onSave,
  modo = "franja",
  fecha,
  horaInicio,
  horaFin,
  esAdmin = false,
  tecnicos = null,
  tecnicoIdActual = null,
  fechaLimiteCampana,
}) {

  const [tecnicoId, setTecnicoId] = useState(tecnicoIdActual);
  const [recurrencia, setRecurrencia] = useState("solo_dia");
  const [rangoDesde, setRangoDesde] = useState(fecha);
  const [rangoHasta, setRangoHasta] = useState(fechaLimiteCampana || fecha);
  const [horaIniEditable, setHoraIniEditable] = useState(horaInicio);
  const [horaFinEditable, setHoraFinEditable] = useState(horaFin);
  const [motivo, setMotivo] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  // Reset al abrir
  useEffect(() => {
    if (!open) return;
    setTecnicoId(tecnicoIdActual);
    setRecurrencia("solo_dia");
    setRangoDesde(fecha);
    setRangoHasta(fechaLimiteCampana || fecha);
    setHoraIniEditable(horaInicio);
    setHoraFinEditable(horaFin);
    setMotivo("");
    setError(null);
  }, [open, fecha, horaInicio, horaFin, tecnicoIdActual, fechaLimiteCampana]);

  const diaISO = useMemo(() => fechaADiaISO(fecha), [fecha]);
  const diaLabel = DIAS_LABEL[diaISO] || "";

  // Fechas calculadas según la recurrencia
  const fechasObjetivo = useMemo(() => {
    if (recurrencia === "solo_dia") return [fecha];
    if (recurrencia === "todos_campana") {
      return fechasRecurrentes(fecha, fechaLimiteCampana, diaISO);
    }
    if (recurrencia === "rango") {
      return fechasRecurrentes(rangoDesde, rangoHasta, diaISO);
    }
    return [];
  }, [recurrencia, fecha, fechaLimiteCampana, diaISO, rangoDesde, rangoHasta]);

  const puedeGuardar =
    !!tecnicoId &&
    !!horaIniEditable && !!horaFinEditable &&
    horaIniEditable < horaFinEditable &&
    fechasObjetivo.length > 0 &&
    !guardando;

  async function guardar() {
    if (!puedeGuardar) return;
    setGuardando(true);
    setError(null);
    try {
      const bloqueos = fechasObjetivo.map((f) => ({
        persona_id: tecnicoId,
        fecha: f,
        hora_inicio: normalizarHora(horaIniEditable),
        hora_fin: normalizarHora(horaFinEditable),
        motivo: motivo.trim() || null,
      }));
      await onSave(bloqueos);
      onClose();
    } catch (e) {
      console.error("[BloquearFranjaModal] guardar", e);
      setError(e?.response?.data?.error || "No se pudo guardar el bloqueo.");
    } finally {
      setGuardando(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50">
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-slate-500" />
            <h2 className="text-base font-semibold text-slate-900">
              {modo === "franja" ? "Bloquear franja" : "Bloquear slot"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Cuerpo */}
        <div className="px-5 py-4 space-y-4">

          {/* Contexto */}
          <div className="bg-slate-50 rounded-md p-3 text-sm">
            <div className="text-slate-700">
              <strong>{diaLabel}</strong>{" "}
              <span className="text-slate-500">
                {fecha && new Date(`${fecha}T12:00:00`).toLocaleDateString("es-ES", {
                  day: "numeric", month: "long",
                })}
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {String(horaInicio).slice(0, 5)} → {String(horaFin).slice(0, 5)}
              {modo === "slot" && (
                <span className="ml-2 text-slate-400">(slot individual)</span>
              )}
            </div>
          </div>

          {/* Selector de técnico (solo admin) */}
          {esAdmin && (
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Técnico a bloquear *
              </label>
              <select
                value={tecnicoId || ""}
                onChange={(e) => setTecnicoId(parseInt(e.target.value, 10) || null)}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
              >
                <option value="">— Selecciona técnico —</option>
                {(tecnicos || []).map((t) => (
                  <option key={t.id} value={t.id}>{t.nombre}</option>
                ))}
              </select>
            </div>
          )}

          {/* Horas (editables si se quiere afinar) */}
          {modo === "franja" && (
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Rango horario
              </label>
              <div className="flex items-center gap-2">
                <TimePicker24h
                  value={horaIniEditable}
                  onChange={setHoraIniEditable}
                  ariaLabel="Hora inicio"
                />
                <span className="text-slate-400 text-sm">→</span>
                <TimePicker24h
                  value={horaFinEditable}
                  onChange={setHoraFinEditable}
                  ariaLabel="Hora fin"
                />
              </div>
              <p className="text-[10px] text-slate-400 mt-1">
                Puedes afinar el rango si el bloqueo no es la franja entera.
              </p>
            </div>
          )}

          {/* Recurrencia */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">
              Aplicar a
            </label>
            <div className="space-y-2">
              <OpcionRecurrencia
                checked={recurrencia === "solo_dia"}
                onClick={() => setRecurrencia("solo_dia")}
                label="Solo este día"
                hint={fecha && new Date(`${fecha}T12:00:00`).toLocaleDateString("es-ES")}
              />
              <OpcionRecurrencia
                checked={recurrencia === "todos_campana"}
                onClick={() => setRecurrencia("todos_campana")}
                label={`Todos los ${diaLabel.toLowerCase()} de la campaña`}
                hint={
                  fechaLimiteCampana
                    ? `Hasta ${new Date(`${fechaLimiteCampana}T12:00:00`).toLocaleDateString("es-ES")}`
                    : "Sin fecha límite definida"
                }
                disabled={!fechaLimiteCampana}
              />
              <OpcionRecurrencia
                checked={recurrencia === "rango"}
                onClick={() => setRecurrencia("rango")}
                label={`Todos los ${diaLabel.toLowerCase()} en un rango personalizado`}
              />

              {recurrencia === "rango" && (
                <div className="grid grid-cols-2 gap-2 ml-7">
                  <div>
                    <label className="block text-[10px] text-slate-500 mb-0.5">Desde</label>
                    <input
                      type="date"
                      value={rangoDesde}
                      onChange={(e) => setRangoDesde(e.target.value)}
                      className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-slate-500 mb-0.5">Hasta</label>
                    <input
                      type="date"
                      value={rangoHasta}
                      onChange={(e) => setRangoHasta(e.target.value)}
                      className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Preview de fechas afectadas */}
            {fechasObjetivo.length > 0 && (
              <p className="text-[11px] text-slate-500 mt-2">
                Se bloquearán{" "}
                <strong className="text-slate-700">{fechasObjetivo.length}</strong>{" "}
                {fechasObjetivo.length === 1 ? "día" : "días"}.
              </p>
            )}
            {fechasObjetivo.length === 0 && recurrencia !== "solo_dia" && (
              <p className="text-[11px] text-amber-700 mt-2">
                Ningún día coincide con el rango seleccionado.
              </p>
            )}
          </div>

          {/* Motivo */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              Motivo (opcional)
            </label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={2}
              maxLength={255}
              placeholder="Ej. Reunión interna, formación, baja médica..."
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md p-2.5 flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-slate-500 hover:text-slate-900 px-3 py-1.5"
            disabled={guardando}
          >
            Cancelar
          </button>
          <Button onClick={guardar} disabled={!puedeGuardar}>
            {guardando ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Lock className="w-3.5 h-3.5" />
                Bloquear{fechasObjetivo.length > 1 && ` ${fechasObjetivo.length} días`}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
//  Sub-componente: opción de recurrencia
// ────────────────────────────────────────────────────────────────

function OpcionRecurrencia({ checked, onClick, label, hint, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "w-full text-left flex items-start gap-2.5 px-3 py-2 rounded-md border transition-colors",
        checked && !disabled && "border-primary bg-primary/5",
        !checked && !disabled && "border-slate-200 hover:border-slate-300",
        disabled && "border-slate-100 bg-slate-50 cursor-not-allowed opacity-60"
      )}
    >
      <span className={cn(
        "shrink-0 w-3.5 h-3.5 rounded-full border-2 mt-0.5",
        checked && !disabled ? "border-primary bg-primary" : "border-slate-300"
      )}>
        {checked && !disabled && (
          <span className="block w-1 h-1 rounded-full bg-white mx-auto mt-[3px]" />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <div className={cn(
          "text-sm",
          checked && !disabled ? "text-slate-900 font-medium" : "text-slate-700"
        )}>
          {label}
        </div>
        {hint && (
          <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>
        )}
      </div>
    </button>
  );
}
